import { observer } from "mobx-react-lite";
import { Box, BoxProps, Card, CardProps, Collapse, IconButton, SvgIcon, Theme, lighten } from "@mui/material";
import React, { useState } from "react";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

// ======================
// CollapsibleCard
// ======================

interface CollapsibleCardProps {
    collapsibleElement: React.ReactNode;
    arrowPlacement: "top-left" | "top-right" | "left-middle" | "right-middle";
    defaultExpanded?: boolean;
    // Recovery-point cards unmount expanded details so operation polling stops
    // when the card is closed. Schedule editors keep their fields mounted.
    unmountOnExit?: boolean;
    // The Compliance timeline keeps the selected card expanded while the
    // marker changes. List cards omit this and stay independently collapsed.
    onExpandedChange?: (expanded: boolean) => void;
}

export const CollapsibleCard: React.FC<CollapsibleCardProps & Partial<CardProps>> = observer((p) => {
    const {
        collapsibleElement,
        arrowPlacement,
        defaultExpanded = false,
        unmountOnExit = false,
        onExpandedChange,
        children,
        // i18n-ignore: HTML attribute key; its value is the caller's localized label or user-entered name.
        "aria-label": ariaLabel,
        ...cardProps
    } = p;
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);
    const icon = isExpanded ? <IoIosArrowUp /> : <IoIosArrowDown />;

    const toggleExpansion = () => {
        const next = !isExpanded;
        setIsExpanded(next);
        onExpandedChange?.(next);
    };

    const alignItems = arrowPlacement?.includes("middle") ? "center" : "flex-start";
    const arrowLeft = arrowPlacement?.includes("left");
    const arrowRight = arrowPlacement?.includes("right");

    return (
        <Card
            {...cardProps}
            aria-label={ariaLabel}
            sx={{
                transition: "all 0.25s ease",
                "&:hover": isExpanded
                    ? {}
                    : {
                          backgroundColor: (t: Theme) => lighten(t.palette.cirrus.light, 0.1),
                      },
                "&:active": isExpanded
                    ? {}
                    : {
                          backgroundColor: (t: Theme) => lighten(t.palette.cirrus.light, 0.15),
                      },
                ...cardProps.sx,
            }}
        >
            <Box display={"flex"} justifyContent={"space-between"} alignItems={alignItems}>
                {arrowLeft ? (
                    <Box p={2}>
                        <IconButton onClick={toggleExpansion} aria-label={ariaLabel} aria-expanded={isExpanded}>
                            <SvgIcon>{icon}</SvgIcon>
                        </IconButton>
                    </Box>
                ) : null}
                <Box onClick={toggleExpansion} sx={{ cursor: "pointer" }} width={"100%"}>
                    {children}
                </Box>
                {arrowRight ? (
                    <Box p={2}>
                        <IconButton onClick={toggleExpansion} aria-label={ariaLabel} aria-expanded={isExpanded}>
                            <SvgIcon>{icon}</SvgIcon>
                        </IconButton>
                    </Box>
                ) : null}
            </Box>
            <Collapse in={isExpanded} unmountOnExit={unmountOnExit}>
                <Box>{collapsibleElement}</Box>
            </Collapse>
        </Card>
    );
});

// ======================
// CollapsibleBox
// ======================

interface CollapsibleBoxProps {
    collapsibleElement: React.ReactNode;
}

export const CollapsibleBox: React.FC<CollapsibleBoxProps & Partial<BoxProps>> = observer((p) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const icon = isExpanded ? <IoIosArrowUp /> : <IoIosArrowDown />;

    const toggleExpansion = () => {
        if (isExpanded) {
            setIsExpanded(false);
        } else {
            setIsExpanded(true);
        }
    };

    return (
        <Box {...p}>
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                {p.children}
                <Box pr={2}>
                    <IconButton onClick={toggleExpansion}>
                        <SvgIcon>{icon}</SvgIcon>
                    </IconButton>
                </Box>
            </Box>
            {isExpanded && <Box>{p.collapsibleElement}</Box>}
        </Box>
    );
});
